import type { LegalCitation, ResearchAnswer, ReviewStatus } from '@buakei/types';
import { Badge, DataOriginBadge, PreviewAction } from '@buakei/ui';
import { AlertTriangle, Info, SearchX } from 'lucide-react';
import { CitationLink } from '@/inspector/CitationLink';
import { resolveCitationSync } from '@/services/legal-sources';
import { DEMO_SOURCES } from '@/demo/legal-sources';

const REVIEW_LABEL: Record<ReviewStatus, string> = {
  ai_draft: 'AI draft — lawyer review required',
  under_review: 'Under review',
  approved_internal: 'Approved for internal use',
  requires_reverification: 'Requires re-verification',
  archived: 'Archived',
};

function UnresolvedCitation({ citation }: { citation: LegalCitation }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-sm">
      <Badge tone="danger">
        <AlertTriangle aria-hidden />
        Unresolved — excluded from evidence
      </Badge>
      <CitationLink citation={citation} />
    </span>
  );
}

/** ResearchAnswer + UncertaintyNotice + CoveragePanel (DESIGN-SYSTEM foundational components). */
export function ResearchAnswerView({
  answer,
  audience = 'professional',
}: {
  answer: ResearchAnswer;
  audience?: 'professional' | 'learning';
}) {
  const byId = new Map(answer.citations.map((c) => [c.id, c]));
  const index = new Map(answer.citations.map((c, i) => [c.id, i + 1]));
  const isDemo = answer.dataOrigin === 'demo';

  return (
    <article className="flex flex-col gap-4" aria-label={`Answer: ${answer.query}`}>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <DataOriginBadge origin={answer.dataOrigin} />
        {audience === 'professional' ? (
          <span className="text-xs font-medium text-navy-900">
            {REVIEW_LABEL[answer.reviewStatus]}
          </span>
        ) : null}
        <span className="text-xs text-muted-600">
          {answer.mode === 'quick' ? 'Quick research' : answer.mode}
        </span>
      </div>

      {answer.outcome === 'not_found' ? (
        <div className="flex gap-3 rounded-lg border border-line-200 bg-white p-4">
          <SearchX className="mt-0.5 size-5 shrink-0 text-navy-900" aria-hidden />
          <div>
            <p className="font-semibold text-navy-900">Not found within the searched sources</p>
            <p className="text-sm text-ink-900">{answer.summary}</p>
          </div>
        </div>
      ) : (
        <p className="font-serif text-lg leading-relaxed text-ink-900">{answer.summary}</p>
      )}

      {answer.propositions.length > 0 ? (
        <ol className="flex flex-col gap-3">
          {answer.propositions.map((p) => (
            <li key={p.id} className="rounded-md border border-line-200 bg-white p-3">
              <p className="text-ink-900">{p.text}</p>
              <div className="mt-2 flex flex-wrap gap-3">
                {p.citationIds.map((cid) => {
                  const citation = byId.get(cid);
                  if (!citation) return null;
                  const resolved =
                    resolveCitationSync(citation, DEMO_SOURCES).status === 'resolved';
                  return resolved ? (
                    <CitationLink key={cid} citation={citation} index={index.get(cid)} />
                  ) : (
                    <UnresolvedCitation key={cid} citation={citation} />
                  );
                })}
              </div>
              {p.caveat ? (
                <p className="mt-2 flex gap-2 text-sm text-muted-600">
                  <Info className="mt-0.5 size-4 shrink-0 text-navy-900" aria-hidden />
                  {p.caveat}
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}

      {answer.coverage.cannotEstablish.length > 0 ? (
        <section
          aria-label="Could not be established"
          className="rounded-md border border-line-200 bg-white p-3"
        >
          <h4 className="text-sm font-semibold text-navy-900">Could not be established</h4>
          <ul className="mt-1 list-disc pl-5 text-sm text-ink-900">
            {answer.coverage.cannotEstablish.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <details className="rounded-md border border-line-200 bg-white p-3 text-sm">
        <summary className="cursor-pointer font-semibold text-navy-900">
          Research scope and known gaps
        </summary>
        <div className="mt-2 grid gap-3 md:grid-cols-2">
          <div>
            <h4 className="font-medium text-navy-900">Collections searched</h4>
            <ul className="list-disc pl-5 text-ink-900">
              {answer.coverage.searchedCollections.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-navy-900">Known gaps</h4>
            {answer.coverage.knownGaps.length > 0 ? (
              <ul className="list-disc pl-5 text-ink-900">
                {answer.coverage.knownGaps.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-600">None recorded for this demo answer.</p>
            )}
          </div>
        </div>
        <p className="mt-2 text-muted-600">Source set snapshot: {answer.sourceSetSnapshotId}</p>
      </details>

      {audience === 'professional' ? (
        <div className="flex flex-wrap gap-4">
          <PreviewAction reason="Not in this demonstration. Save answers from Ask Buakei as Research memos.">
            Save to research history
          </PreviewAction>
          <PreviewAction
            reason={
              isDemo ? 'Demo content can never be exported.' : 'Export answers from Research memos.'
            }
          >
            Export memo
          </PreviewAction>
        </div>
      ) : null}
    </article>
  );
}
